import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import MembersFlatTable from '../MembersFlatTable';
import SuitcaseIcon from 'icons/Suitcase.icon';
import ResolvePopup from 'components/ResolvePopup';
import PlusIcon from 'icons/Plus.icon';
import LinkIcon from 'icons/Link.icon';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import {
  DataRoomBuySideGroup,
  PreviousGroupChats,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import {
  getArrayLength,
  getFilterOptionByValue,
  getHasActiveFilters,
  getIsArrayEmpty,
} from 'utils';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import MultiSortPanel from 'components/MultiSortPanel';
import MultiFilterPanel from 'components/MultiFilterPanel';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useMultiSort from 'hooks/useMultiSort';
import useMembers from '../../../../hooks/useMembers';
import useMemberDrawers from '../../hooks/useMemberDrawers';
import useBuySideMemberFilters from '../../hooks/useBuySideMemberFilters';
import useRenameGroupSuggestion from '../../hooks/useRenameGroupSuggestion';
import AddGroupMember from '../AddGroupMember';
import MergeBuySideGroups from '../MergeBuySideGroups';
import ManageBuyerGroup from '../ManageBuyerGroup';
import {
  BUY_SIDE_PAGE_PARAM,
  BUY_SIDE_SORT_COLUMNS,
  BUY_SIDE_SORT_PARAM,
  MEMBERS_TABLE_LIMIT,
  MULTI_SORT_DIRECTION_COPY,
  MULTI_SORT_LABELS,
  SORTING_VALUES,
  BUY_SIDE_FILTER_PARAMS,
  DEFAULT_HIDDEN_COLUMNS,
  MANAGE_COLUMNS_LABELS,
  REQUIRED_COLUMN,
} from '../MembersFlatTable/data';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import useQueryParams from 'hooks/useQueryParams';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import ManageColumns from 'components/ManageColumns';
import { getMemberTableColumns } from '../MembersFlatTable/utils';

import './BuySide.styles.scss';

const BUY_SIDE_COLUMN_FLAGS = {
  showGroup: true,
  showActivity: true,
  showChat: true,
};

type BuySideProps = {
  className?: string;
};

const BuySide: React.FC<BuySideProps> = (props) => {
  const { className } = props;

  const [selectedGroup, setSelectedGroup] = useState<DataRoomBuySideGroup>();

  const { doesntHaveAccess } = useContext(DataRoomContext);

  const [addMember, addMemberRef] = useCallbackRef<SideDrawerRef>(null);
  const [mergeGroups, mergeGroupsRef] = useCallbackRef<SideDrawerRef>(null);
  const [groupDrawer, groupDrawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { buySideGroups, buySideMembers } = useMembers();

  const {
    selectedMemberId,
    onEdit,
    onDeleteMember,
    onAcceptRequest,
    onRejectRequest,
    onEditMemberRole,
    renderDrawers,
  } = useMemberDrawers({
    isBuySide: true,
  });

  const classes = classNames('buy-side', 'white-panel', className);

  const { memberGroupMap, previousGroupChatsMap } = useMemo(() => {
    const groupMap: Record<string, DataRoomBuySideGroup> = {};
    const chatsMap: Record<string, PreviousGroupChats> = {};

    buySideGroups.forEach((group) => {
      chatsMap[group._id] = {
        activeChatSessionId: group.activeChatSessionId,
        closedChatSessions: group.closedChatSessions ?? [],
      };

      group.dataRoomMembers.forEach((member) => {
        groupMap[member._id] = group;
      });
    });

    return {
      memberGroupMap: groupMap,
      previousGroupChatsMap: chatsMap,
    };
  }, [buySideGroups]);

  // Only a member who can rename a group is worth nudging about it.
  const { renameSuggestionGroupId, dismissRenameSuggestion } =
    useRenameGroupSuggestion(buySideGroups, !doesntHaveAccess);

  const { filterOptions, filteredMembers } = useBuySideMemberFilters({
    members: buySideMembers,
    buySideGroups,
    memberGroupMap,
  });

  const { params } = useQueryParams();

  const isEmpty = getIsArrayEmpty(buySideMembers);

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, params),
    [filterOptions, params],
  );

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    BUY_SIDE_SORT_COLUMNS,
    SORTING_VALUES,
    MULTI_SORT_LABELS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const buySideColumns = useMemo(
    () => getMemberTableColumns(BUY_SIDE_COLUMN_FLAGS),
    [],
  );

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.BUY_SIDE_MEMBERS,
    buySideColumns,
    DEFAULT_HIDDEN_COLUMNS,
  );

  const columnFilterOptions = useMemo(
    () => ({
      name: {
        options: getFilterOptionByValue(
          filterOptions,
          BUY_SIDE_FILTER_PARAMS.NAME,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.name],
      },
      group: {
        options: getFilterOptionByValue(
          filterOptions,
          BUY_SIDE_FILTER_PARAMS.GROUP,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.group],
      },
      files: {
        options: getFilterOptionByValue(
          filterOptions,
          BUY_SIDE_FILTER_PARAMS.FILES_ROLE,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.files],
      },
      qa: {
        options: getFilterOptionByValue(
          filterOptions,
          BUY_SIDE_FILTER_PARAMS.QA_ROLE,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.qa],
      },
      status: {
        options: getFilterOptionByValue(
          filterOptions,
          BUY_SIDE_FILTER_PARAMS.STATUS,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.status],
      },
      lastSeen: {
        options: getFilterOptionByValue(
          filterOptions,
          BUY_SIDE_FILTER_PARAMS.LAST_SEEN,
        ),
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.lastSeen],
      },
      views: {
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.views],
      },
      questionsColumn: {
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.questionsColumn],
      },
      downloads: {
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.downloads],
      },
    }),
    [filterOptions, multiSortDirectionTranslation],
  );

  const onGroupClick = (group: DataRoomBuySideGroup) => {
    setSelectedGroup(group);
    groupDrawer?.setOpen(true);
  };

  const showAddGroupButton = !doesntHaveAccess;
  const showMergeGroupsButton =
    !doesntHaveAccess && getArrayLength(buySideGroups) >= 2;

  return (
    <section className={classes}>
      <div className="members-panel__header">
        <div className="members-panel__title">
          <SuitcaseIcon />
          <p className="name">{t('buySide')}</p>

          <ResolvePopup onHover popupContent={t('buySideMembersPopup')}>
            <InformationcircleIcon />
          </ResolvePopup>

          <p className="count">
            {`${t('numOfMembers', {
              count: getArrayLength(buySideMembers),
            })} ${t('in')} ${t('numOfGroups', {
              count: getArrayLength(buySideGroups),
            })}`}
          </p>
        </div>

        <div className="members-panel__actions">
          {showMergeGroupsButton && (
            <Button icon={LinkIcon} onClick={() => mergeGroups?.setOpen(true)}>
              {t('mergeGroups')}
            </Button>
          )}

          {showAddGroupButton && (
            <Button icon={PlusIcon} onClick={() => addMember?.setOpen(true)}>
              {t('addMember')}
            </Button>
          )}
        </div>
      </div>

      <div className="members-panel__toolbar">
        <ActiveFilterSortPills
          filterOptions={filterOptions}
          sortOptions={multiSortValues}
          sortParam={BUY_SIDE_SORT_PARAM}
          onChipLabelClick={onChipLabelClick}
          onSortChipLabelClick={onSortChipLabelClick}
        />

        {(!isEmpty || hasActiveFilters) && (
          <div className="members-panel__toolbar__actions">
            <MultiFilterPanel ref={multiFilterRef} filters={filterOptions} />

            <MultiSortPanel
              ref={multiSortRef}
              options={multiSortValues}
              sortDirectionTranslations={multiSortDirectionTranslation}
              sortParam={BUY_SIDE_SORT_PARAM}
            />

            <ManageColumns
              allColumns={allColumns}
              hiddenColumns={hiddenColumns}
              headingTranslations={MANAGE_COLUMNS_LABELS}
              lockedColumn={REQUIRED_COLUMN}
              isDefaultColumns={isDefaultColumns}
              onSave={saveColumnSettings}
              onResetToDefault={resetToDefault}
            />
          </div>
        )}
      </div>

      <MembersFlatTable
        isBuySide
        {...BUY_SIDE_COLUMN_FLAGS}
        sortParam={BUY_SIDE_SORT_PARAM}
        pageParam={BUY_SIDE_PAGE_PARAM}
        limit={MEMBERS_TABLE_LIMIT}
        columns={columns}
        columnSizing={columnSizing}
        onReorder={onReorder}
        onColumnSizingChange={onColumnSizingChange}
        canManageColumns
        columnFilterOptions={columnFilterOptions}
        members={filteredMembers}
        memberGroupMap={memberGroupMap}
        previousGroupChatsMap={previousGroupChatsMap}
        renameSuggestionGroupId={renameSuggestionGroupId}
        selectedMemberId={selectedMemberId}
        onDismissRenameSuggestion={dismissRenameSuggestion}
        onRowClick={onEdit}
        onEdit={onEdit}
        onDeleteMember={onDeleteMember}
        onAcceptRequest={onAcceptRequest}
        onRejectRequest={onRejectRequest}
        onEditMemberRole={onEditMemberRole}
        onGroupClick={onGroupClick}
        renderDrawers={renderDrawers}
      />

      <AddGroupMember ref={addMemberRef} />
      <MergeBuySideGroups ref={mergeGroupsRef} />
      <ManageBuyerGroup ref={groupDrawerRef} group={selectedGroup} />
    </section>
  );
};

export default BuySide;
